<template>
  <div class="purchase-flow">
    <!-- 页面标题 -->
    <div class="page-hero">
      <div class="hero-content">
        <h1 class="hero-title">{{ $t('buy.hero.title') }}</h1>
        <p class="hero-subtitle">{{ $t('buy.hero.subtitle') }}</p>
      </div>
      <div class="hero-decoration">
        <div class="deco-circle c1"></div>
        <div class="deco-circle c2"></div>
        <div class="deco-circle c3"></div>
      </div>
    </div>

    <!-- 步骤条 -->
    <div class="steps-container">
      <div class="mobile-step-progress">
        <div class="mobile-step-meta">
          <span>{{ activeStep + 1 }} / {{ steps.length }}</span>
          <strong>{{ steps[activeStep].title }}</strong>
        </div>
        <div class="mobile-step-track">
          <span :style="{ width: mobileStepProgress }"></span>
        </div>
        <p>{{ steps[activeStep].subtitle }}</p>
      </div>
      <el-steps 
        :active="activeStep" 
        align-center 
        finish-status="success"
        process-status="process"
        class="custom-steps">
        <el-step 
          v-for="(step, index) in steps" 
          :key="index"
          :title="step.title" 
          :icon="step.icon">
          <template slot="description">
            <div class="step-subtitle">{{ step.subtitle }}</div>
          </template>
        </el-step>
      </el-steps>
    </div>

    <!-- 内容区域 -->
    <div class="content-container">
      <transition :name="transitionName" mode="out-in">
        <div class="step-panel">
          <keep-alive :key="purchaseRevision">
            <component
              :is="currentStepComponent"
              :key="currentStepKey"
              v-bind="currentStepProps"
              v-on="currentStepListeners"
            />
          </keep-alive>

          <!-- 底部汇总 -->
          <OrderSummary
            v-if="activeStep < 5"
            :orderData="orderSummary"
            :purchaseConfig="purchaseConfig"
            :activeStep="activeStep"
            :selectedStandards="selectedStandards.standards"
            :selectedLanguages="selectedLanguages.languages"
          />
        </div>
      </transition>
    </div>

    <!-- 导航按钮 (支付步骤不显示) -->
    <div class="action-buttons" v-if="activeStep < 5">
      <el-button 
        v-if="activeStep > 0"
        @click="prevStep"
        class="nav-button prev-button"
        icon="el-icon-arrow-left">
        {{ $t('buy.nav.prev') }}
      </el-button>
      <div class="button-spacer" v-else></div>
      <el-button 
        v-if="activeStep < 4"
        type="primary" 
        @click="nextStep"
        :disabled="!canContinue || loadingPurchaseConfig || !hasAvailablePlan"
        class="nav-button next-button">
        {{ $t('buy.nav.next') }}
        <i class="el-icon-arrow-right"></i>
      </el-button>
      <el-button
        v-if="activeStep === 4"
        type="primary"
        @click="goToPayment"
        :loading="creatingOrder"
        :disabled="loadingPurchaseConfig || !hasAvailablePlan"
        class="nav-button submit-button">
        <i class="el-icon-shopping-cart-2"></i>
        {{ $t('buy.nav.completePurchase') }}
      </el-button>
    </div>

    <!-- 底部保障信息 -->
    <div class="trust-bar">
      <div class="trust-item">
        <i class="el-icon-lock"></i>
        <span>{{ $t('buy.trust.secure') }}</span>
      </div>
      <!-- <div class="trust-item">
        <i class="el-icon-refresh-left"></i>
        <span>{{ $t('buy.trust.moneyBack') }}</span>
      </div> -->
      <div class="trust-item">
        <i class="el-icon-service"></i>
        <span>{{ $t('buy.trust.support') }}</span>
      </div>
      <div class="trust-item">
        <i class="el-icon-check"></i>
        <span>{{ $t('buy.trust.cancel') }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import brand from '@/branding'
import PlanSelect from './PlanSelect.vue'
import LanguageSelect from './LanguageSelect.vue'
import StandardSelect from './StandardSelect.vue'
import SeatConfig from './SeatConfig.vue'
import OrderConfirm from './OrderConfirm.vue'
import PaymentStep from './PaymentStep.vue'
import OrderSummary from './OrderSummary.vue'
import request from '@/utils/http'
import { displayCurrencyLabel } from '@/utils/currency'
import { getToken, getUserInfo } from '@/utils/auth'
import iamApi from '@/api/iam'

const BUY_DRAFT_KEY = 'lpt_buy_draft_v1'
const REVIEW_STEP_INDEX = 4
const FALLBACK_LANGUAGE_IDS = [
  'english',
  'french',
  'spanish',
  'chinese',
  'german',
  'polish',
  'czech',
  'slovak',
  'hungarian',
  'romanian',
  'bulgarian',
  'serbian',
  'croatian',
  'slovenian'
]

export default {
  name: 'BuyFlow',
  components: {
    PlanSelect,
    LanguageSelect,
    StandardSelect,
    SeatConfig,
    OrderConfirm,
    PaymentStep,
    OrderSummary
  },
  data() {
    return {
      activeStep: 0,
      canContinue: true,
      transitionDirection: 'forward',
      stepValidity: {
        0: true,
        1: true,
        2: true,
        3: true,
        4: true,
        5: true
      },
      selectedPlan: {
        planId: 'professional',
        billingCycle: 'one_year',
        price: 0
      },
      selectedStandards: {
        standards: ['iso9001'],
        totalPrice: 0
      },
      selectedLanguages: {
        languages: ['english'],
        languageLimit: 2
      },
      enterpriseLanguageDefaultActive: false,
      seatsConfig: {
        auditorSeats: 1,
        extraAdminSeats: 0,
        totalPrice: 0
      },
      companyInfo: {
        companyName: '',
        legalRepresentative: '',
        registrationNumber: '',
        taxId: '',
        address: '',
        countryCode: '86',
        phone: '',
        wechat: '',
        email: '',
        contactName: ''
      },
      orderConfirmState: {
        termsAccepted: false,
        promoCode: ''
      },
      purchaseConfig: null,
      loadingPurchaseConfig: true,
      purchaseConfigRequestId: 0,
      purchaseRevision: 0,
      selectedCurrency: brand.id === 'mt' ? 'EUR' : 'CNY',
      createdOrder: null,
      creatingOrder: false
    }
  },
  created() {
    const restoredPaymentReturn = this.restorePaymentReturn()
    const restoredDraft = !restoredPaymentReturn && this.shouldRestoreBuyDraft() && this.restoreBuyDraft()
    if (!restoredDraft && !restoredPaymentReturn) {
      this.clearBuyDraft()
    }
    this.fetchPurchaseConfig()
    if (!restoredDraft && !restoredPaymentReturn) {
      this.hydrateAutoCompanyInfo()
    }
    if (restoredDraft || restoredPaymentReturn) {
      this.clearBuyDraft()
    }
  },
  computed: {
    displayCurrency() {
      const raw = this.isMtBrand ? this.selectedCurrency :
        (this.purchaseConfig && this.purchaseConfig.displayCurrency) || this.selectedCurrency
      return displayCurrencyLabel(raw)
    },
    paymentCurrency() {
      return (this.purchaseConfig && this.purchaseConfig.paymentCurrency) || this.selectedCurrency
    },
    hasAvailablePlan() {
      return !!(this.purchaseConfig && this.purchaseConfig.plans &&
        this.purchaseConfig.plans[this.selectedPlan.planId])
    },
    isMtBrand() {
      return brand.id === 'mt'
    },
    currencyOptions() {
      return this.isMtBrand
        ? [
            { value: 'EUR', label: 'EUR / €' },
            { value: 'CHF', label: 'CHF / Fr.' }
          ]
        : [{ value: 'CNY', label: 'RMB' }]
    },
    orderFinalAmount() {
      const is2Years = this.selectedPlan.billingCycle === 'two_year'
      const planPrice = this.selectedPlan.price
      const stdPrice = is2Years ? this.selectedStandards.totalPrice * 2 : this.selectedStandards.totalPrice
      const seatsPrice = is2Years ? this.seatsConfig.totalPrice * 2 : this.seatsConfig.totalPrice
      return planPrice + stdPrice + seatsPrice
    },
    finalDisplayAmount() {
      return this.createdOrder ? this.fromMinor(this.createdOrder.finalAmount) : this.orderFinalAmount
    },
    steps() {
      return [
        {
          title: this.$t('buy.steps.plan.title'),
          subtitle: this.$t('buy.steps.plan.subtitle'),
          icon: 'el-icon-s-grid'
        },
        {
          title: this.$t('buy.steps.languages.title'),
          subtitle: this.$t('buy.steps.languages.subtitle'),
          icon: 'el-icon-chat-line-square'
        },
        {
          title: this.$t('buy.steps.standards.title'),
          subtitle: this.$t('buy.steps.standards.subtitle'),
          icon: 'el-icon-document'
        },
        {
          title: this.$t('buy.steps.seats.title'),
          subtitle: this.$t('buy.steps.seats.subtitle'),
          icon: 'el-icon-user'
        },
        {
          title: this.$t('buy.steps.confirm.title'),
          subtitle: this.$t('buy.steps.confirm.subtitle'),
          icon: 'el-icon-circle-check'
        },
        {
          title: this.$t('buy.steps.payment.title'),
          subtitle: this.$t('buy.steps.payment.subtitle'),
          icon: 'el-icon-wallet'
        }
      ]
    },
    mobileStepProgress() {
      return `${((this.activeStep + 1) / this.steps.length) * 100}%`
    },
    orderSummary() {
      return {
        serviceName: this.$t(`buy.plan.plans.${this.selectedPlan.planId}.name`),
        planId: this.selectedPlan.planId,
        billingCycle: this.selectedPlan.billingCycle,
        planPrice: this.selectedPlan.price,
        standards: this.selectedStandards.standards,
        standardsPrice: this.selectedStandards.totalPrice,
        languages: this.selectedLanguages.languages,
        languageLimit: this.selectedLanguages.languageLimit,
        auditorSeats: this.seatsConfig.auditorSeats,
        includedAdminSeats: 0,
        extraAdminSeats: this.seatsConfig.extraAdminSeats,
        seatsPrice: this.seatsConfig.totalPrice,
        company: this.companyInfo,
        orderNo: this.createdOrder && this.createdOrder.orderNo,
        currency: this.displayCurrency
      }
    },
    transitionName() {
      return this.transitionDirection === 'forward' ? 'slide-left' : 'slide-right'
    },
    currentStepKey() {
      return `buy-step-${this.activeStep}`
    },
    currentStepComponent() {
      const stepComponents = [
        'PlanSelect',
        'LanguageSelect',
        'StandardSelect',
        'SeatConfig',
        'OrderConfirm',
        'PaymentStep'
      ]
      return stepComponents[this.activeStep]
    },
    currentStepProps() {
      const common = {
        purchaseConfig: this.purchaseConfig,
        currency: this.displayCurrency
      }
      if (this.activeStep === 0) {
        return {
          ...common,
          selectedPlanData: this.selectedPlan,
          selectedCurrency: this.selectedCurrency,
          currencyOptions: this.currencyOptions,
          showCurrencySelector: this.isMtBrand
        }
      }
      if (this.activeStep === 1) {
        return { ...common, planId: this.selectedPlan.planId, selectedValue: this.selectedLanguages.languages }
      }
      if (this.activeStep === 2) {
        return { ...common, selectedValue: this.selectedStandards.standards }
      }
      if (this.activeStep === 3) {
        return { ...common, planId: this.selectedPlan.planId, value: this.seatsConfig }
      }
      if (this.activeStep === 4) {
        return { ...common, orderData: this.orderSummary, confirmState: this.orderConfirmState }
      }
      if (this.activeStep === 5) {
        return {
          ...common,
          finalAmount: this.finalDisplayAmount,
          orderData: this.orderSummary,
          orderNo: this.createdOrder && this.createdOrder.orderNo
        }
      }
      return common
    },
    currentStepListeners() {
      const listeners = {
        validate: this.handleValidate
      }

      if (this.activeStep === 0) {
        listeners['plan-selected'] = this.handlePlanSelected
        listeners['currency-change'] = this.handleCurrencyChange
      }
      if (this.activeStep === 1) {
        listeners['languages-selected'] = this.handleLanguagesSelected
      }
      if (this.activeStep === 2) {
        listeners['standards-selected'] = this.handleStandardsSelected
      }
      if (this.activeStep === 3) {
        listeners['seats-configured'] = this.handleSeatsConfigured
      }
      if (this.activeStep === 4) {
        listeners['order-confirm-updated'] = this.handleOrderConfirmUpdated
      }
      if (this.activeStep === 5) {
        listeners['payment-success'] = this.handlePaymentSuccess
      }

      return listeners
    }
  },
  methods: {
    nextStep() {
      if (this.loadingPurchaseConfig || !this.hasAvailablePlan || !this.canContinue) return
      if (this.activeStep < this.steps.length - 1) {
        this.transitionDirection = 'forward'
        this.activeStep++
        this.syncCurrentStepValidity()
        this.scrollToTop()
      }
    },
    prevStep() {
      if (this.activeStep > 0) {
        this.transitionDirection = 'backward'
        this.activeStep--
        this.syncCurrentStepValidity()
        this.scrollToTop()
      }
    },
    scrollToTop() {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    handleValidate(valid) {
      this.$set(this.stepValidity, this.activeStep, valid)
      this.canContinue = valid
    },
    syncCurrentStepValidity() {
      this.canContinue = this.stepValidity[this.activeStep] !== false
    },
    handlePlanSelected(data) {
      const previousPlanId = this.selectedPlan.planId
      if (data.planId === 'enterprise' && previousPlanId !== data.planId) {
        this.enterpriseLanguageDefaultActive = true
      }
      if (data.planId !== 'enterprise') {
        this.enterpriseLanguageDefaultActive = false
      }
      this.selectedPlan = {
        planId: data.planId,
        billingCycle: data.billingCycle,
        price: data.price
      }
      this.enforceLanguageLimit()
    },
    handleLanguagesSelected(data) {
      const languages = Array.isArray(data && data.languages) ? [...data.languages] : []
      const rawLanguageLimit = data ? data.languageLimit : null
      const languageLimit = rawLanguageLimit == null ? null : Number(rawLanguageLimit)
      const currentLimit = this.selectedLanguages.languageLimit == null ? null : Number(this.selectedLanguages.languageLimit)
      const hasSameLanguages = this.isSameArray(this.selectedLanguages.languages, languages)
      if (this.selectedPlan.planId === 'enterprise') {
        this.enterpriseLanguageDefaultActive = this.isSameArray(languages, this.getAllLanguageIds())
      } else {
        this.enterpriseLanguageDefaultActive = false
      }
      if (hasSameLanguages && currentLimit === languageLimit) {
        return
      }
      this.selectedLanguages = {
        languages,
        languageLimit
      }
    },
    handleStandardsSelected(data) {
      this.selectedStandards = data
    },
    handleSeatsConfigured(data) {
      this.seatsConfig = data
    },
    handleOrderConfirmUpdated(data) {
      this.companyInfo = data.company
      this.orderConfirmState = {
        termsAccepted: data.termsAccepted,
        promoCode: data.promoCode || ''
      }
    },
    async hydrateAutoCompanyInfo() {
      const cachedUserInfo = getUserInfo() || {}
      this.companyInfo = this.buildAutoCompanyInfo(cachedUserInfo)
      if (!getToken()) {
        return
      }
      try {
        const profile = await iamApi.getCurrentUser()
        this.companyInfo = this.buildAutoCompanyInfo({
          ...cachedUserInfo,
          ...this.mapProfileToBuyerInfo(profile)
        })
      } catch (e) {
        console.warn('Failed to load current user profile for buy flow', e)
      }
    },
    buildAutoCompanyInfo(source = {}) {
      const email = source.email || ''
      const emailName = email ? email.split('@')[0] : ''
      const contactName = source.contactName || source.name || emailName || source.sub || 'Account User'
      return {
        companyName: source.companyName || contactName || 'Current Account',
        legalRepresentative: source.legalRepresentative || '',
        registrationNumber: source.registrationNumber || '',
        taxId: source.taxId || '',
        address: source.address || 'Auto-generated from logged-in account',
        countryCode: source.countryCode || '86',
        phone: source.phone || 'N/A',
        wechat: source.wechat || '',
        email: email || 'account@auto.local',
        contactName
      }
    },
    mapProfileToBuyerInfo(profile = {}) {
      const fullName = [
        profile.fullName && profile.fullName.firstName,
        profile.fullName && profile.fullName.lastName
      ].filter(Boolean).join(' ').trim()
      const organization = profile.organization || {}
      const addressObject = profile.address || organization.address || {}
      const address = [
        addressObject.addressLine1,
        addressObject.addressLine2,
        addressObject.city,
        addressObject.stateOrProvince,
        addressObject.postalCode,
        addressObject.countryOrRegion
      ].filter(Boolean).join(', ')
      return {
        companyName: organization.name || profile.companyName || profile.organizationName || '',
        legalRepresentative: organization.legalRepresentative || '',
        registrationNumber: organization.registrationNumber || profile.registrationNumber || '',
        taxId: organization.taxId || profile.taxId || '',
        address,
        countryCode: organization.countryCode || profile.countryCode || '',
        phone: organization.phoneNumber || profile.phoneNumber || profile.telephoneNumber || '',
        wechat: organization.wechat || profile.wechat || '',
        email: profile.email || organization.email || '',
        contactName: fullName || profile.name || ''
      }
    },
    handleCurrencyChange(currency) {
      if (!this.currencyOptions.some(option => option.value === currency) || currency === this.selectedCurrency) return
      this.selectedCurrency = currency
      this.purchaseRevision++
      this.activeStep = 0
      this.transitionDirection = 'forward'
      this.createdOrder = null
      this.orderConfirmState = { termsAccepted: false, promoCode: '' }
      this.stepValidity = { 0: false, 1: true, 2: false, 3: true, 4: false, 5: false }
      this.canContinue = false
      this.clearBuyDraft()
      this.selectedPlan = {
        planId: 'professional',
        billingCycle: 'one_year',
        price: 0
      }
      this.selectedStandards = {
        standards: ['iso9001'],
        totalPrice: 0
      }
      this.selectedLanguages = {
        languages: ['english'],
        languageLimit: 2
      }
      this.seatsConfig = {
        auditorSeats: 1,
        extraAdminSeats: 0,
        totalPrice: 0
      }
      this.enterpriseLanguageDefaultActive = false
      this.fetchPurchaseConfig()
    },
    async fetchPurchaseConfig() {
      const currency = this.selectedCurrency
      const requestId = ++this.purchaseConfigRequestId
      this.loadingPurchaseConfig = true
      // 清空旧价目表，避免把旧币种金额显示或提交为新币种。
      this.purchaseConfig = {
        displayCurrency: this.displayCurrency,
        paymentCurrency: currency,
        plans: {},
        standards: [],
        auditorSeatPrices: [],
        adminSeatPrice: 0
      }
      try {
        const endpoint = process.env.VUE_APP_PURCHASE_CONFIG_PATH || '/api/payment/purchase-config'
        const config = await request.get(endpoint, { params: { brandCode: brand.id, currency } })
        if (requestId !== this.purchaseConfigRequestId) return
        this.purchaseConfig = {
          ...config,
          displayCurrency: this.isMtBrand ? currency : config.displayCurrency || currency
        }
        const plans = config.plans || {}
        const planId = plans[this.selectedPlan.planId] ? this.selectedPlan.planId : Object.keys(plans)[0]
        const plan = plans[planId]
        this.selectedPlan = {
          ...this.selectedPlan,
          planId: planId || 'professional',
          price: plan ? this.fromMinor(this.selectedPlan.billingCycle === 'two_year' ? plan.twoYears : plan.oneYear) : 0
        }
        this.$set(this.stepValidity, 0, !!plan)
        this.syncCurrentStepValidity()
        this.enforceLanguageLimit()
      } catch (e) {
        if (requestId !== this.purchaseConfigRequestId) return
        console.error('Failed to load purchase config', e)
        this.$message.error(this.$t('buy.errors.loadPurchaseConfig'))
      } finally {
        if (requestId === this.purchaseConfigRequestId) this.loadingPurchaseConfig = false
      }
    },
    fromMinor(amount) {
      return Math.round(Number(amount || 0)) / 100
    },
    isSameArray(first, second) {
      if (!Array.isArray(first) || !Array.isArray(second)) return false
      return first.length === second.length && first.every((item, index) => item === second[index])
    },
    clonePlain(data) {
      return JSON.parse(JSON.stringify(data))
    },
    getBuyDraftPayload() {
      return {
        activeStep: REVIEW_STEP_INDEX,
        selectedPlan: this.clonePlain(this.selectedPlan),
        selectedLanguages: this.clonePlain(this.selectedLanguages),
        selectedStandards: this.clonePlain(this.selectedStandards),
        seatsConfig: this.clonePlain(this.seatsConfig),
        companyInfo: this.clonePlain(this.companyInfo),
        orderConfirmState: this.clonePlain(this.orderConfirmState)
      }
    },
    shouldRestoreBuyDraft() {
      const restoreFlag = this.$route && this.$route.query && this.$route.query.restoreBuyDraft
      const normalizedFlag = Array.isArray(restoreFlag) ? restoreFlag[0] : restoreFlag
      return normalizedFlag === '1'
    },
    restorePaymentReturn() {
      const query = (this.$route && this.$route.query) || {}
      const orderNo = Array.isArray(query.order_no) ? query.order_no[0] : query.order_no
      const hasPaymentReturn = query.alipay_return === '1' ||
        query.payment_method === 'alipay' ||
        query.payment_intent_client_secret ||
        query.checkout_session_id
      if (!hasPaymentReturn || !orderNo) {
        return false
      }
      this.createdOrder = { orderNo }
      this.activeStep = 5
      this.transitionDirection = 'forward'
      this.$set(this.stepValidity, 5, true)
      this.syncCurrentStepValidity()
      this.fetchReturnedOrder(orderNo)
      return true
    },
    async fetchReturnedOrder(orderNo) {
      try {
        this.createdOrder = await request.get(`/api/payment/orders/${orderNo}`)
      } catch (e) {
        console.warn('Failed to load returned payment order', e)
      }
    },
    saveBuyDraft() {
      if (typeof window === 'undefined' || !window.localStorage) return
      try {
        window.localStorage.setItem(BUY_DRAFT_KEY, JSON.stringify(this.getBuyDraftPayload()))
      } catch (e) {
        console.warn('Failed to save buy draft', e)
      }
    },
    restoreBuyDraft() {
      if (typeof window === 'undefined' || !window.localStorage) return false
      try {
        const rawDraft = window.localStorage.getItem(BUY_DRAFT_KEY)
        if (!rawDraft) return false
        const draft = JSON.parse(rawDraft)
        if (!draft || typeof draft !== 'object') return false

        if (draft.selectedPlan && typeof draft.selectedPlan === 'object') {
          this.selectedPlan = {
            ...this.selectedPlan,
            ...draft.selectedPlan
          }
        }
        if (draft.selectedStandards && typeof draft.selectedStandards === 'object') {
          this.selectedStandards = {
            ...this.selectedStandards,
            ...draft.selectedStandards,
            standards: Array.isArray(draft.selectedStandards.standards)
              ? [...draft.selectedStandards.standards]
              : this.selectedStandards.standards
          }
        }
        if (draft.selectedLanguages && typeof draft.selectedLanguages === 'object') {
          this.selectedLanguages = {
            ...this.selectedLanguages,
            ...draft.selectedLanguages,
            languages: Array.isArray(draft.selectedLanguages.languages)
              ? [...draft.selectedLanguages.languages]
              : this.selectedLanguages.languages
          }
          this.enforceLanguageLimit()
        }
        if (draft.seatsConfig && typeof draft.seatsConfig === 'object') {
          this.seatsConfig = {
            ...this.seatsConfig,
            ...draft.seatsConfig
          }
        }
        if (draft.companyInfo && typeof draft.companyInfo === 'object') {
          this.companyInfo = {
            ...this.companyInfo,
            ...draft.companyInfo
          }
        }
        if (draft.orderConfirmState && typeof draft.orderConfirmState === 'object') {
          this.orderConfirmState = {
            ...this.orderConfirmState,
            ...draft.orderConfirmState
          }
        }

        this.activeStep = REVIEW_STEP_INDEX
        this.transitionDirection = 'forward'
        this.$set(this.stepValidity, REVIEW_STEP_INDEX, !!this.orderConfirmState.termsAccepted)
        this.syncCurrentStepValidity()
        return true
      } catch (e) {
        console.warn('Failed to restore buy draft', e)
        return false
      }
    },
    clearBuyDraft() {
      if (typeof window === 'undefined' || !window.localStorage) return
      try {
        window.localStorage.removeItem(BUY_DRAFT_KEY)
      } catch (e) {
        console.warn('Failed to clear buy draft', e)
      }
    },
    async goToPayment() {
      if (this.loadingPurchaseConfig || !this.hasAvailablePlan || this.creatingOrder) return
      if (!this.canContinue) {
        this.$alert(this.$t('buy.order.agreementRequired'), this.$t('buy.order.termsOfService'), {
          confirmButtonText: this.$t('login.close') || 'OK'
        })
        return
      }
      if (!getToken()) {
        this.saveBuyDraft()
        this.$router.push({
          path: '/login',
          query: {
            redirect: '/buy',
            restoreBuyDraft: '1'
          }
        })
        return
      }
      const purchaseRevision = this.purchaseRevision
      try {
        this.creatingOrder = true
        const order = await request.post('/api/payment/orders', {
          brandCode: brand.id,
          planId: this.selectedPlan.planId,
          billingCycle: this.selectedPlan.billingCycle,
          paymentMode: 'ONE_TIME',
          paymentCurrency: this.paymentCurrency,
          catalogCurrency: this.paymentCurrency,
          currency: this.selectedCurrency,
          languages: this.selectedLanguages.languages,
          standards: this.selectedStandards.standards,
          auditorSeats: this.seatsConfig.auditorSeats,
          extraAdminSeats: this.seatsConfig.extraAdminSeats,
          promoCode: this.orderConfirmState.promoCode,
          termsAccepted: this.orderConfirmState.termsAccepted,
          company: this.companyInfo,
          purchaseSnapshot: {
            selectedPlan: this.selectedPlan,
            selectedLanguages: this.selectedLanguages,
            selectedStandards: this.selectedStandards,
            seatsConfig: this.seatsConfig,
            clientFinalAmount: this.orderFinalAmount
          }
        })
        if (purchaseRevision !== this.purchaseRevision) return
        this.createdOrder = order
      } catch (e) {
        if (purchaseRevision !== this.purchaseRevision) return
        console.error('Failed to create order', e)
        this.$message.error(this.$t('buy.errors.createOrder'))
        return
      } finally {
        this.creatingOrder = false
      }
      this.clearBuyDraft()
      this.transitionDirection = 'forward'
      this.activeStep = 5
      this.syncCurrentStepValidity()
      this.scrollToTop()
    },
    handlePaymentSuccess(paymentData) {
      console.log('Payment success:', paymentData)
    },
    getPlanLanguageLimit(planId) {
      const plan = this.purchaseConfig && this.purchaseConfig.plans && this.purchaseConfig.plans[planId]
      if (plan && Object.prototype.hasOwnProperty.call(plan, 'languageLimit')) {
        return plan.languageLimit
      }
      const fallback = {
        starter: 1,
        professional: 2,
        enterprise: null
      }
      return Object.prototype.hasOwnProperty.call(fallback, planId) ? fallback[planId] : 1
    },
    getAllLanguageIds() {
      const configLanguages = this.purchaseConfig && Array.isArray(this.purchaseConfig.languages)
        ? this.purchaseConfig.languages
        : []
      if (!configLanguages.length) {
        return [...FALLBACK_LANGUAGE_IDS]
      }
      return configLanguages.map(language => language.id || language.code).filter(Boolean)
    },
    enforceLanguageLimit(options = {}) {
      const limit = this.getPlanLanguageLimit(this.selectedPlan.planId)
      const isUnlimited = limit == null || Number(limit) <= 0
      const shouldSelectAll = (options.defaultAllForEnterprise || this.enterpriseLanguageDefaultActive) &&
        this.selectedPlan.planId === 'enterprise'
      let languages = shouldSelectAll
        ? this.getAllLanguageIds()
        : Array.isArray(this.selectedLanguages.languages) && this.selectedLanguages.languages.length
        ? [...this.selectedLanguages.languages]
        : ['english']
      if (!isUnlimited && languages.length > Number(limit)) {
        languages = languages.slice(0, Number(limit))
      }
      const nextLanguages = {
        languages,
        languageLimit: isUnlimited ? null : Number(limit)
      }
      const currentLimit = this.selectedLanguages.languageLimit == null ? null : Number(this.selectedLanguages.languageLimit)
      if (
        this.isSameArray(this.selectedLanguages.languages, nextLanguages.languages) &&
        currentLimit === nextLanguages.languageLimit
      ) {
        return
      }
      this.selectedLanguages = nextLanguages
    }
  }
}
</script>

<style lang="scss" scoped>
.purchase-flow {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 30px 60px;
  min-height: 100vh;
}

// Hero Section
.page-hero {
  position: relative;
  text-align: center;
  padding: 120px 0 50px;
  overflow: hidden;

  .hero-content {
    position: relative;
    z-index: 2;
  }

  .hero-title {
    font-size: 38px;
    font-weight: 800;
    color: #1e293b;
    margin: 0 0 14px;
    letter-spacing: -0.5px;
    
    background: linear-gradient(135deg, #1e293b 0%, #6366f1 50%, #8b5cf6 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  .hero-subtitle {
    font-size: 17px;
    color: #64748b;
    margin: 0;
    max-width: 600px;
    margin: 0 auto;
    line-height: 1.6;
  }
}

.hero-decoration {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;

  .deco-circle {
    position: absolute;
    border-radius: 50%;
    opacity: 0.06;

    &.c1 {
      width: 400px;
      height: 400px;
      background: #6366f1;
      top: -100px;
      right: -100px;
    }
    &.c2 {
      width: 300px;
      height: 300px;
      background: #8b5cf6;
      top: 50px;
      left: -80px;
    }
    &.c3 {
      width: 200px;
      height: 200px;
      background: #3b82f6;
      bottom: -50px;
      right: 200px;
    }
  }
}

// Steps
.steps-container {
  max-width: 900px;
  margin: 0 auto 50px;
  padding: 20px 30px;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 4px 24px rgba(0,0,0,0.04);
  border: 1px solid #f1f5f9;
}

.mobile-step-progress {
  display: none;
}

.custom-steps {
  ::v-deep .el-step {
    min-width: 0;
  }

  ::v-deep .el-step__main {
    min-width: 0;
    padding: 0 6px;
  }

  ::v-deep .el-step__head {
    .el-step__icon {
      width: 44px;
      height: 44px;
      font-size: 20px;
      border: 2px solid;
      transition: all 0.3s;
    }

    &.is-process .el-step__icon {
      background: linear-gradient(135deg, #6366f1, #818cf8);
      border-color: transparent;
      color: white;
      box-shadow: 0 4px 12px rgba(99,102,241,0.3);
    }

    &.is-success .el-step__icon {
      background: #10b981;
      border-color: #10b981;
      color: white;
    }
  }

  ::v-deep .el-step__title {
    font-size: 15px;
    font-weight: 600;
    color: #64748b;
    line-height: 1.35;
    word-break: break-word;
    
    &.is-process {
      color: #6366f1;
      font-weight: 700;
    }
    
    &.is-success {
      color: #10b981;
    }
  }

  ::v-deep .el-step__description {
    line-height: 1.4;
    word-break: break-word;
  }

  .step-subtitle {
    color: #94a3b8;
    font-size: 12px;
    margin-top: 4px;
  }
}

// Content
.content-container {
  background: #fff;
  border-radius: 20px;
  padding: 40px;
  box-shadow: 0 4px 24px rgba(0,0,0,0.04);
  border: 1px solid #f1f5f9;
  min-height: 500px;
  margin-bottom: 30px;
}

// Transitions
.slide-left-enter-active,
.slide-left-leave-active,
.slide-right-enter-active,
.slide-right-leave-active {
  transition: all 0.35s ease;
}

.slide-left-enter {
  transform: translateX(40px);
  opacity: 0;
}
.slide-left-leave-to {
  transform: translateX(-40px);
  opacity: 0;
}

.slide-right-enter {
  transform: translateX(-40px);
  opacity: 0;
}
.slide-right-leave-to {
  transform: translateX(40px);
  opacity: 0;
}

// Navigation Buttons
.action-buttons {
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 900px;
  margin: 0 auto 30px;
  
  .button-spacer {
    width: 1px;
  }

  .nav-button {
    padding: 14px 32px;
    font-size: 15px;
    font-weight: 600;
    border-radius: 12px;
    transition: all 0.3s;
    
    &:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 8px 20px rgba(0,0,0,0.1);
    }
  }

  .prev-button {
    background: #fff;
    border: 2px solid #e2e8f0;
    color: #475569;

    &:hover {
      border-color: #6366f1;
      color: #6366f1;
    }
  }

  .next-button {
    background: linear-gradient(135deg, #6366f1, #818cf8);
    border: none;
    color: white;
    
    &:hover:not(:disabled) {
      box-shadow: 0 8px 24px rgba(99,102,241,0.35);
    }
  }

  .submit-button {
    background: linear-gradient(135deg, #10b981, #34d399);
    border: none;
    color: white;
    padding: 14px 40px;
    font-size: 16px;

    &:hover:not(:disabled) {
      box-shadow: 0 8px 24px rgba(16,185,129,0.35);
    }
    
    i {
      margin-right: 6px;
    }
  }
}

// Trust Bar
.trust-bar {
  display: flex;
  justify-content: center;
  gap: 40px;
  padding: 20px 0;
  border-top: 1px solid #f1f5f9;

  .trust-item {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: #94a3b8;

    i {
      font-size: 16px;
      color: #10b981;
    }
  }
}

@media (max-width: 991px) {
  .purchase-flow {
    padding: 0 20px 120px;
  }

  .page-hero {
    padding: calc(var(--site-mobile-nav-height) + 2.625rem) 0 2rem;

    .hero-title {
      font-size: 30px;
      letter-spacing: 0;
    }

    .hero-subtitle {
      font-size: 15px;
    }
  }

  .hero-decoration {
    display: none;
  }

  .steps-container {
    max-width: 100%;
    margin-bottom: 24px;
    padding: 18px;
    overflow: hidden;
    border-radius: 14px;
    box-shadow: none;
  }

  .mobile-step-progress {
    display: block;

    .mobile-step-meta {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 14px;
      color: #64748b;
      font-size: 13px;

      > span {
        flex-shrink: 0;
      }

      strong {
        flex: 1;
        min-width: 0;
        color: #1e293b;
        font-size: 16px;
        font-weight: 700;
        text-align: right;
        word-break: break-word;
      }
    }

    .mobile-step-track {
      height: 6px;
      margin: 14px 0 10px;
      overflow: hidden;
      border-radius: 999px;
      background: #e2e8f0;

      span {
        display: block;
        height: 100%;
        border-radius: inherit;
        background: linear-gradient(90deg, #2563eb, #14b8a6);
        transition: width 0.3s ease;
      }
    }

    p {
      margin: 0;
      color: #64748b;
      font-size: 13px;
      line-height: 1.5;
    }
  }

  .custom-steps {
    display: none;
  }

  .content-container {
    padding: 24px;
    min-height: 0;
    border-radius: 16px;
  }

  .action-buttons {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 20;
    max-width: none;
    gap: 12px;
    margin: 0;
    padding: 12px 20px calc(12px + env(safe-area-inset-bottom));
    background: rgba(255, 255, 255, 0.96);
    border-top: 1px solid #e2e8f0;
    box-shadow: 0 -8px 22px rgba(15, 23, 42, 0.08);
    backdrop-filter: blur(10px);

    .button-spacer {
      display: none;
    }

    .nav-button {
      flex: 1;
      min-height: 48px;
      padding: 12px 18px;
      border-radius: 10px;

      &:hover:not(:disabled) {
        transform: none;
      }
    }
  }

  .trust-bar {
    flex-wrap: wrap;
    gap: 14px 22px;
  }
}

@media (max-width: 640px) {
  .purchase-flow {
    padding: 0 12px 112px;
  }

  .page-hero {
    padding: calc(var(--site-mobile-nav-height) + 1.25rem) 0 1rem;
    text-align: left;

    .hero-title {
      font-size: 22px;
      background: none;
      -webkit-text-fill-color: #1e293b;
    }

    .hero-subtitle {
      font-size: 14px;
      line-height: 1.5;
    }
  }

  .steps-container {
    margin: 0 0 14px;
    padding: 14px;
  }

  .content-container {
    padding: 14px 12px;
    margin-bottom: 14px;
    border-radius: 14px;
    box-shadow: none;
  }

  .action-buttons {
    align-items: center;
    flex-direction: row;
    padding-left: 12px;
    padding-right: 12px;

    .nav-button {
      width: 100%;
      min-width: 0;
      padding-left: 10px;
      padding-right: 10px;
    }

    .prev-button {
      flex: 0 0 35%;
      border-width: 1px;
    }

    .next-button,
    .submit-button {
      flex: 1 1 auto;
    }
  }

  .trust-bar {
    align-items: flex-start;
    flex-direction: column;
    padding-left: 4px;
  }
}
</style>
